import React, { useState, useEffect } from 'react';
import { i18next, i18n, dialogs, integration, readOnly } from '@yola/ws-sdk';
import { selectors } from '@yola/subscription-manager-js';
import { useDispatch, useSelector } from 'react-redux';
import yolaEditorDialogs from 'yola-editor/src/js/modules/dialogs';
import { PUBLISHING } from 'yola-editor/src/js/modules/upsell/constants/upsell-types';
import yolaEditorSite from 'yola-editor/src/js/modules/site';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import hasSiteCustomDomain from 'yola-editor/src/js/modules/publishing/verifiers/has-site-custom-domain';
import useMatchBreakpoint from 'src/js/modules/utils/custom-hooks/use-match-breakpoint';
import segment from 'src/js/modules/analytics/segment';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import previewMode from 'src/js/modules/preview-mode';
import showReadOnlyModeDialog from 'src/js/modules/read-only-mode/helpers/show-read-only-mode-dialog';
import getPublishUrl from 'src/js/modules/editor/helpers/get-publish-url';
import PublishInterruptionDialog from '../components/publish-interruption-dialog';
import publishInterruptionTypes from '../constants/publish-interruption-types';
import getPublishInterruptionOptions from '../helpers/get-publish-interruption-options';
import dialogTypes from '../../dialogs/constants/dialog-types';

const {
  trackers: { trackEvent },
  constants: { events, triggerIds },
} = segment;

const getCaptions = () => ({
  title: i18next.t('Publish your site to show it to the world!'),
  buttonLabel: i18next.t('Continue'),
  closeButtonLabel: i18next.t('Back'),
  domainSelectorTitle: i18next.t('Choose Your Domain'),
});
const DESKTOP_BREAKPOINT = '992px';
const TABLET_BREAKPOINT = '576px';
const DESKTOP_MODAL_WIDTH = 814;
const MOBILE_MODAL_WIDTH = 500;
const DEFAULT_LANGUAGE_CODE = 'en';
const LOCALIZED_IMAGES = ['en', 'cs', 'de', 'es', 'fr', 'it', 'pt-br', 'sk', 'sv'];

const PublishInterruptionDialogContainer = () => {
  const [selectedOption, setSelectedOption] = useState(publishInterruptionTypes.PUBLISH);

  const dispatch = useDispatch();
  const packageType = useSelector(selectors.getCurrentPackageType);
  const upsells = useSelector(integration.selectors.getUpsells);
  const siteURL = useSelector(yolaEditorSite.selectors.getSiteURL);
  const activeHostingSubscription = useSelector(selectors.getActiveHostingSubscription);
  const isPublishedToCustomDomain = useSelector(hasSiteCustomDomain);
  const isReadOnlyEnabled = useSelector(readOnly.selectors.getReadOnlyStatus);

  const isDesktopView = useMatchBreakpoint(DESKTOP_BREAKPOINT);
  const isTabletView = useMatchBreakpoint(TABLET_BREAKPOINT);
  const [featureFlags] = useFeatureFlags(['upgrade_to_publish']);

  const { upgrade_to_publish: isUpgradeToPublishEnabled } = featureFlags;

  const captions = getCaptions();
  const options = getPublishInterruptionOptions();
  const locale = i18n.accessors.getLocale();
  const languageCode = LOCALIZED_IMAGES[locale] || DEFAULT_LANGUAGE_CODE;
  const modalWidth = isDesktopView ? DESKTOP_MODAL_WIDTH : MOBILE_MODAL_WIDTH;
  const traits = { activeHostingSubscription: packageType };

  const onSubmit = () => {
    trackEvent(events.PUBLISH_INTERRUPTION_CONTINUE_CLICKED, traits);

    if (selectedOption === publishInterruptionTypes.PUBLISH) {
      const showPublishInterruption = () => {
        dispatch(dialogs.actions.show(dialogTypes.PUBLISH_INTERRUPTION_DIALOG));
      };

      if (
        !activeHostingSubscription &&
        (isPublishedToCustomDomain || (isUpgradeToPublishEnabled && siteURL))
      ) {
        upsells.publishing
          .onUpgrade({
            successUrl: getPublishUrl(),
            isReactivatingSubscription: true,
          })
          .then(Function.prototype, showPublishInterruption);
      } else {
        dispatch(
          yolaEditorDialogs.actions.show(yolaEditorDialogs.dialogTypes.B2C_DOMAIN_PURCHASE, {
            triggerId: triggerIds.PUBLISH_INTERRUPTION,
            shouldPublishOnSuccess: true,
            withFreeSubdomain: true,
            withPurchasedDomains: true,
            premiumFeature: PUBLISHING,
            captionsOverrides: {
              closeButton: captions.closeButtonLabel,
              title: captions.domainSelectorTitle,
            },
            onClose: showPublishInterruption,
          })
        );
      }

      dialogs.operations.hide();
    }

    if (selectedOption === publishInterruptionTypes.PREVIEW) {
      if (isReadOnlyEnabled) {
        showReadOnlyModeDialog();
        dialogs.operations.hide();
        return;
      }
      saveSiteWithEffect(() => {
        dispatch(previewMode.thunks.generateSitePreview());
      });

      dialogs.operations.hide();
    }
  };

  const onClose = () => {
    trackEvent(events.PUBLISH_INTERRUPTION_CLOSED, traits);
    dialogs.operations.hide();
  };

  const onClick = (id) => {
    setSelectedOption(id);

    if (id === publishInterruptionTypes.PUBLISH) {
      trackEvent(events.PUBLISH_INTERRUPTION_CUSTOM_DOMAIN_CLICKED, traits);
    }

    if (id === publishInterruptionTypes.PREVIEW) {
      trackEvent(events.PUBLISH_INTERRUPTION_PREVIEW_CLICKED, traits);
    }
  };

  useEffect(() => {
    trackEvent(events.PUBLISH_INTERRUPTION_DISPLAYED, traits);
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <PublishInterruptionDialog
      selectedOption={selectedOption}
      isTabletView={isTabletView}
      modalWidth={modalWidth}
      captions={captions}
      options={options}
      languageCode={languageCode}
      desktopBreakpoint={DESKTOP_BREAKPOINT}
      onSubmit={onSubmit}
      onClose={onClose}
      onClick={onClick}
    />
  );
};

export default PublishInterruptionDialogContainer;
